import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {notifier} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {BACKGROUND_RECONNECTION_ATTEMPTS, RECONNECTION_ATTEMPTS} from 'constants/socket';
import {AnalyticsOnboardingEventName, LogoutType, SocketDisconnectReason, SocketEvent, SocketManagerEvent} from 'enums';
import {usePrevious, useSocketEventCallback, useUserSocket} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {authDataStore, accountPlanStore, socketStore, analyticsService} from 'services';
import {AccountPlanStore, AuthDataStore, User} from 'types';
import {logger, userAuthUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMMON.MESSAGES.CONNECTION;

type Props = {
    onChange: (socket: SocketLikeWithNamespace) => void;
}

export const UserSocketConnection: React.FC<Props> = ({onChange}: Props) => {
    const {t} = useTranslation();
    const [accessToken, setAccessToken] = useState(authDataStore.getValue().accessToken);
    const [refreshTokenTriggered, setRefreshTokenTriggered] = useState(false);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const prevReconnectAttempt = usePrevious(reconnectAttempt);
    const [connectionNotifierId, setConnectionNotifierId] = useState<React.ReactText>();
    const {socket, connected} = useUserSocket(accessToken, {enabled: true});
    const shouldShowReconnectNotification = useMemo(() => reconnectAttempt > BACKGROUND_RECONNECTION_ATTEMPTS, [reconnectAttempt]);
    const history = useHistory();

    useEffect(() => logger.debug(`Socket connection state changed. Connected: ${connected}. ID: ${socket.id}`), [connected, socket.id]);

    const refreshAccessToken = useCallback(async () => {
        try {
            setAccessToken(null);
            setRefreshTokenTriggered(true);
            await userAuthUtils.refreshAccessToken();
            setAccessToken(authDataStore.getValue().accessToken);
        } catch {
            userAuthUtils.logout(LogoutType.TokenExpired);
        }
    }, []);

    const onCloseConnectionNotifier = useCallback(() => {
        setConnectionNotifierId(null);
    }, []);

    useSocketEventCallback(socket, SocketEvent.Connect, useCallback(() => {
        if (shouldShowReconnectNotification) {
            const toastProps = {title: t(T_KEYS.RESTORED)};
            if (connectionNotifierId) {
                notifier.wrap(connectionNotifierId).onSuccess(toastProps);
            } else {
                notifier.success(toastProps);
            }
        }
        setReconnectAttempt(0);
        socketStore.mergeValue({connected: true});
    }, [t, shouldShowReconnectNotification, connectionNotifierId]));

    useSocketEventCallback(socket, SocketEvent.Authenticated,
        useCallback((payload: {accountDetails: AccountPlanStore, isFirstAccountLogin?: boolean}) => {
            logger.debug(`Received socket event: ${SocketEvent.Authenticated}`);
            socketStore.mergeValue({authenticated: true});
            if (refreshTokenTriggered) {
                setRefreshTokenTriggered(false);
            }
            if (payload?.accountDetails) {
                const {planId, limits, usage, hasBillingPlatformProfile} = payload.accountDetails;
                accountPlanStore.mergeValue({planId, limits, usage, hasBillingPlatformProfile});

                if (payload?.isFirstAccountLogin) {
                    analyticsService.trackOnboardingEvent(AnalyticsOnboardingEventName.FirstLogin, {
                        plan_id: planId
                    });
                }
            }
        }, [refreshTokenTriggered]));

    useSocketEventCallback(socket, SocketEvent.Unauthorized, useCallback((error: any) => {
        logger.debug(`Received socket event: ${SocketEvent.Unauthorized}`, error);
        socketStore.mergeValue({authenticated: false});
        if (!refreshTokenTriggered) {
            logger.debug('Trigger refresh token');
            refreshAccessToken();
        } else {
            userAuthUtils.logout(LogoutType.TokenExpired);
        }
    }, [refreshTokenTriggered, refreshAccessToken]));

    useSocketEventCallback(socket, SocketManagerEvent.ReconnectFailed, useCallback(() => {
        logger.debug(`Received socket event: ${SocketManagerEvent.ReconnectFailed}`);
        history.push(ROUTE_PATH.USER.ERROR.SERVER_CONNECTION);
    }, [history]));

    useSocketEventCallback(socket, SocketEvent.ProfileDeleted, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.ProfileDeleted}`);
        userAuthUtils.logout(LogoutType.TokenExpired);
    }, []));

    useSocketEventCallback(socket, SocketManagerEvent.ReconnectAttempt, useCallback((attempt: number) => {
        logger.debug(`Received socket event: ${SocketManagerEvent.ReconnectAttempt}`, attempt);
        setReconnectAttempt(attempt);
    }, []));

    useSocketEventCallback(socket, SocketEvent.Disconnect, useCallback((reason: SocketDisconnectReason) => {
        const willReconnect = !(socket.io as any).skipReconnect; // remove when typings fixed in socket.io
        logger.debug(`Received socket event: ${SocketEvent.Disconnect} with reason "${reason}". Will reconnect: `, willReconnect);
        socketStore.mergeValue({connected: false, authenticated: false});
    }, [socket]));

    useSocketEventCallback(socket, SocketEvent.Logout, useCallback(() => {
        logger.debug(`Received socket event: ${SocketEvent.Logout}.`);
        userAuthUtils.logout(LogoutType.ByServer);
    }, []));

    useSocketEventCallback(socket, SocketEvent.ProfileChanged, useCallback((user: User) => {
        logger.debug(`Received socket event: ${SocketEvent.ProfileChanged}`);
        userAuthUtils.setLocalUser(user);
    }, []));

    useSocketEventCallback(socket, SocketEvent.AccountPlanLimitsChanged, useCallback((changes: {inSessionMinutes: number}) => {
        if (changes?.inSessionMinutes) {
            const {inSessionMinutes} = changes;
            accountPlanStore.mergeValue({usage: {inSessionMinutes}});
            logger.debug(`Received socket event: ${SocketEvent.AccountPlanLimitsChanged}. Wasted ${Math.round(inSessionMinutes)} minutes`);
        }
    }, []));

    const onAuthDataStoreUpdated = useCallback((value: AuthDataStore, prevValue: AuthDataStore) => {
        if (value.accessToken !== prevValue?.accessToken) {
            setAccessToken(value.accessToken);
        }
        if (!value.localUser) {
            accountPlanStore.resetValue();
        }
    }, []);

    useEffect(() => authDataStore.subscribe(onAuthDataStoreUpdated), [onAuthDataStoreUpdated]);

    useEffect(() => {
        if (reconnectAttempt && reconnectAttempt > prevReconnectAttempt) {
            if (reconnectAttempt === RECONNECTION_ATTEMPTS) {
                const toastProps = {title: t(T_KEYS.FAILED), details: t(T_KEYS.REFRESH_REQUIRED)};
                if (connectionNotifierId) {
                    notifier.wrap(connectionNotifierId).onError(toastProps, {autoClose: false});
                } else {
                    notifier.error(toastProps);
                }
            } else if (shouldShowReconnectNotification) {
                const toastProps = {title: t(T_KEYS.INTERRUPTED), details: t(T_KEYS.RECONNECTING)};
                if (connectionNotifierId) {
                    notifier.wrap(connectionNotifierId).onLoading(toastProps, {autoClose: false});
                } else {
                    const {id} = notifier.loading(toastProps, {autoClose: false, onClose: onCloseConnectionNotifier});
                    setConnectionNotifierId(id);
                }
            }
        }
    }, [t, connectionNotifierId, reconnectAttempt, prevReconnectAttempt, shouldShowReconnectNotification, onCloseConnectionNotifier]);

    useEffect(() => {
        onChange(socket);
    }, [socket, onChange]);

    return null;
};